import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Paper,
  CardHeader,
  CardContent,
  Typography,
} from '@mui/material';
import Loader from 'components/Loader/Loader';
import { API_URL } from '../../configs/Api';
import { getHeaders } from '../../middleware/Api';
import { Integration, Credentials } from './types';
import CredentialsFlow from './CredentialsFlow';

const styles = require('./styles.pcss');

type Props = {
  organizationId: string;
  enqueueSnackBarMessage: Function;
};

const HrisManager: React.FC<Props> = (props) => {
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    const fetchData = async function () {
      setIsLoading(true);
      try {
        const response = await fetch(
          `${API_URL}organizations/${props.organizationId}/credentials`,
          { headers: getHeaders() },
        );
        const data = await response.json();
        if (data.type === 'data') {
          const integrations = data.payload.map(
            ({ integration, credential }) => ({
              ...integration,
              credentials: credential,
            }),
          );
          setIntegrations(integrations);
        } else {
          throw new Error(data.data);
        }
      } catch (error: any) {
        props.enqueueSnackBarMessage(
          'Error loading HRIS data: ' + error?.message,
          'error',
        );
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  const usernameTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>(
    {},
  );

  const handleFieldChange = (
    integrationId: string,
    field: string,
    value: string,
  ) => {
    let credentialId = '';
    setIntegrations((prev) =>
      prev.map((int) => {
        if (int.id !== integrationId) return int;
        credentialId = int.credentials.id;
        if (field === 'enabled') {
          return { ...int, enabled: value === 'enabled' };
        }
        if (field === 'credentials') {
          return {
            ...int,
            credentials: { ...int.credentials, username: value } as any,
          };
        }
        return { ...int, [field]: value };
      }),
    );
    switch (field) {
      case 'enabled': {
        fetch(`${API_URL}credentials/toggle/${integrationId}`, {
          headers: getHeaders(),
          method: 'PUT',
        })
          .then((res) => res.json())
          .then((data) => {
            if (data.type !== 'data') throw new Error(data.data);
          })
          .catch((e) =>
            props.enqueueSnackBarMessage(
              `Error updating status: ${e?.message || e}`,
              'error',
            ),
          );
        break;
      }
      case 'credentials': {
        if (usernameTimers.current[integrationId]) {
          clearTimeout(usernameTimers.current[integrationId]);
        }
        usernameTimers.current[integrationId] = setTimeout(() => {
          fetch(`${API_URL}credentials/${credentialId}`, {
            headers: { ...getHeaders(), 'Content-Type': 'application/json' },
            method: 'PUT',
            body: JSON.stringify({ username: value }),
          })
            .then((res) => res.json())
            .then((data) => {
              if (data.type !== 'data') throw new Error(data.payload.error);
            })
            .catch((e) => {
              if (e?.message.includes('username_1 dup key')) {
                const username = e.message.match(/username: \"(.*)"/)?.[1];
                return props.enqueueSnackBarMessage(
                  `username ${username} is already taken`,
                  'error',
                );
              }
              return props.enqueueSnackBarMessage(
                `Error updating username: ${e?.payload?.error || e}`,
                'error',
              );
            });
        }, 500);
        break;
      }
    }
  };

  const handleDelete = async (integrationId: string) => {
    try {
      const response = await fetch(`${API_URL}credentials/${integrationId}`, {
        headers: getHeaders(),
        method: 'DELETE',
      });
      const data = await response.json();
      if (data.type !== 'data') throw new Error(data.data);
      setIntegrations(integrations.filter((int) => int.id !== integrationId));
    } catch (e) {
      props.enqueueSnackBarMessage(
        `Error deleting integration: ${e?.payload?.error || e}`,
      );
    }
  };

  return (
    <Paper className={styles.hrisManagerContainer}>
      <CardHeader
        className={styles.header}
        title="SFTP Integration Manager"
      />

      <CardContent>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setModalOpen(true)}
          className={styles.createButton}
        >
          Create Credentials
        </Button>

        {isLoading ? (
          <div className={styles.infoLoader}>
            <Loader visible={true} />
          </div>
        ) : (
          <Table className={styles.table}>
            <TableHead>
              <TableRow>
                <TableCell className={styles.headerCell}>
                  Integration ID
                </TableCell>
                <TableCell className={styles.headerCell}>Status</TableCell>
                <TableCell className={styles.headerCell}>Type</TableCell>
                <TableCell className={styles.headerCell}>Username</TableCell>
                <TableCell className={styles.headerCell}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {integrations.map((integration) => (
                <TableRow
                  key={integration.id}
                  className={styles.row}
                >
                  <TableCell className={styles.cell}>
                    {integration.id}
                  </TableCell>
                  <TableCell className={styles.cell}>
                    <Select
                      size="small"
                      value={integration.enabled ? 'enabled' : 'disabled'}
                      onChange={(e) =>
                        handleFieldChange(
                          integration.id,
                          'enabled',
                          e.target.value,
                        )
                      }
                    >
                      <MenuItem value="enabled">Enabled</MenuItem>
                      <MenuItem value="disabled">Disabled</MenuItem>
                    </Select>
                  </TableCell>
                  <TableCell className={styles.cell}>
                    {integration.type}
                  </TableCell>
                  <TableCell className={styles.cell}>
                    <TextField
                      size="small"
                      value={integration.credentials?.username || ''}
                      onChange={(e) =>
                        handleFieldChange(
                          integration.id,
                          'credentials',
                          e.target.value,
                        )
                      }
                    />
                  </TableCell>
                  <TableCell className={styles.cell}>
                    <Button
                      variant="contained"
                      color="primary"
                      className={styles.actionButton}
                      onClick={() => handleDelete(integration.id)}
                      disabled={integration.enabled}
                    >
                      Delete
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
      <CredentialsFlow
        open={modalOpen}
        handleClose={() => setModalOpen(false)}
        sendAlert={props.enqueueSnackBarMessage}
        organizationId={props.organizationId}
        onSuccess={(payload) => {
          setIntegrations((prev) => [
            ...prev,
            { ...payload.integration, credentials: payload.credential },
          ]);
        }}
      />
    </Paper>
  );
};

export default HrisManager;
